import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./register.css";
import { fetchRoles, registerUser, updateUser, fetchTenants } from "../../services/authService";
import { isBootstrapAdmin, getTenantId } from "../../utils/authStorage";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Eye, EyeOff } from "lucide-react";

function RegistrationPage({ isModal, onClose, reloadUserList, selectedUser }) {
  const { t } = useTranslation();
  const [roles, setRoles] = useState([]);
  const [role, setRole] = useState("");
  const [tenants, setTenants] = useState([]);
  const [tenant, setTenant] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [first_name, setFirstName] = useState("");
  const [last_name, setLastName] = useState("");
  const [is_staff, setIsStaff] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [id, setId] = useState("");
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  // Staff/superuser flags and the root role are platform-admin only (server enforces).
  const canSetStaff = isBootstrapAdmin();

  useEffect(() => {
    if (isBootstrapAdmin()) {
      fetchTenants()
        .then((data) => setTenants(data || []))
        .catch((err) => console.error("Failed to load tenants:", err));
    }
  }, []);

  useEffect(() => {
    if (selectedUser && selectedUser.id) {
      setIsEdit(true);
      setId(selectedUser.id);
      setUsername(selectedUser.username || "");
      setEmail(selectedUser.email || "");
      setFirstName(selectedUser.first_name || "");
      setLastName(selectedUser.last_name || "");
      setRole(selectedUser.role ? String(selectedUser.role) : "");
      setTenant(selectedUser.tenant || selectedUser.tenant_name || (isBootstrapAdmin() ? "" : getTenantId()) || "");
      setIsStaff(selectedUser.is_staff || false);
    } else {
      setIsEdit(false);
      setId("");
      setUsername("");
      setEmail("");
      setPassword("");
      setFirstName("");
      setLastName("");
      setTenant(isBootstrapAdmin() ? "" : (getTenantId() || ""));
      setIsStaff(false);
      setRole(null);
    }
  }, [selectedUser]);


  useEffect(() => {
    const loadRoles = async () => {
      let roleData = await fetchRoles();
      roleData = roleData.map((r) => ({
        ...r,
        id: String(r.id),
        name: r.name.toLowerCase(),
      }));
      if (!canSetStaff) roleData = roleData.filter((r) => r.name !== "root");
      roleData.sort((a, b) => a.name.localeCompare(b.name));
      setRoles(roleData);


    };

    loadRoles();
  }, [canSetStaff]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};


    if (!username.trim()) {
      newErrors.username = t("validation.usernameRequired");
    } else if (username.length < 4 || username.length > 20) {
      newErrors.username = t("validation.usernameLength");
    } else if (!/^[\w.@+-]+$/.test(username)) {
      newErrors.username = t("validation.usernameChars");
    }


    if (!email.trim()) {
      newErrors.email = t("validation.emailRequired");
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = t("validation.emailInvalid");
    }

    if (!isEdit && !password.trim()) {
      newErrors.password = t("validation.passwordRequired");
    } else if (password && password.length < 8) {
      newErrors.password = t("validation.passwordLength");
    } else if (password && password.length > 24) {
      newErrors.password = t("validation.passwordMaxLength");
    }

    if (!role) newErrors.role = t("validation.roleRequired");
    if (isBootstrapAdmin() && !isEdit && !tenant) {
      newErrors.tenant = "Please select a tenant for this user.";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      toast.error("Please fix the highlighted errors.");
      return;
    }

    const payload = { id, username, email, first_name, last_name, role };
    if (canSetStaff) payload.is_staff = is_staff;
    const effectiveTenant = tenant || (!isBootstrapAdmin() ? getTenantId() : "");
    if (effectiveTenant) payload.tenant = effectiveTenant;
    if (!isEdit || (isEdit && password.trim() !== "")) payload.password = password;

    try {
      let response = isEdit ? await updateUser(payload) : await registerUser(payload);
      const responseData = await response.json().catch(() => null);

      if (!response.ok) {
        if (response.status === 400 && responseData && typeof responseData === "object") {
          const backendErrors = {};
          for (const [key, value] of Object.entries(responseData)) {
            backendErrors[key] = Array.isArray(value) ? value.join(", ") : value;
          }
          setErrors(backendErrors);
          toast.error(t("label.fixErrors"));
        } else {
          const errorMsg = responseData?.detail || responseData?.error || t("label.unknownError");
          toast.error(errorMsg);
        }
        if (response.status === 500) toast.error(t("label.serverError"));
        return;
      }

      toast.success(isEdit ? t("label.userUpdated") : t("label.userRegistered"));
      if (onClose) onClose();
      if (reloadUserList) reloadUserList();
    } catch (error) {
      console.error("Submission failed:", error);
      toast.error(t("label.submissionError"));
    }
  };

  return (
    <div className={isModal ? "modal-form-wrapper" : "register-wrapper"}>
      <form className={`form-container ${isModal ? "dark-theme" : ""}`} onSubmit={handleSubmit}>

        {/* Organization / Tenant Field */}
        {isBootstrapAdmin() ? (
          <div className="form-group mb-3">
            <label htmlFor="InputTenant">
              Organization / Tenant {!isEdit && <span className="text-danger">*</span>}
            </label>
            <select
              id="InputTenant"
              className={`form-control ${errors.tenant ? "is-invalid" : ""}`}
              value={tenant}
              onChange={(e) => setTenant(e.target.value)}
              disabled={isEdit}
            >
              <option value="">-- Select Organization / Tenant --</option>
              {tenants.map((tItem) => (
                <option key={tItem.id} value={tItem.id}>
                  {tItem.display_name} ({tItem.id}) [{tItem.domain || "surveillance"}]
                </option>
              ))}
            </select>
            {errors.tenant && <div className="invalid-feedback">{errors.tenant}</div>}
          </div>
        ) : (
          <div className="form-group mb-3">
            <label htmlFor="InputTenant">
              Organization / Tenant
            </label>
            <input
              type="text"
              id="InputTenant"
              className="form-control"
              value={tenant || selectedUser?.tenant || getTenantId() || "—"}
              disabled
              readOnly
            />
            <small className="form-text text-muted">Assigned tenant for this account.</small>
          </div>
        )}

        <div className="form-row mb-3">
          <div className="form-group flex-fill">
            <label htmlFor="InputFirstName">First Name</label>
            <input
              type="text"
              className={`form-control ${errors.first_name ? "is-invalid" : ""}`}
              id="InputFirstName"
              placeholder={t("placeholder.enterFirstName")}
              value={first_name}
              onChange={(e) => setFirstName(e.target.value)}
            />
            {errors.first_name && <div className="invalid-feedback">{errors.first_name}</div>}
          </div>


          <div className="form-group flex-fill">
            <label htmlFor="InputLastName">Last Name</label>
            <input
              type="text"
              className={`form-control ${errors.last_name ? "is-invalid" : ""}`}
              id="InputLastName"
              placeholder={t("placeholder.enterLastName")}
              value={last_name}
              onChange={(e) => setLastName(e.target.value)}
            />
            {errors.last_name && <div className="invalid-feedback">{errors.last_name}</div>}
          </div>
        </div>


        <div className="form-group mb-3">
          <label htmlFor="InputUsername">
            User Name {!isEdit && <span className="text-danger">*</span>}
          </label>
          <input
            type="text"
            className={`form-control ${errors.username ? "is-invalid" : ""}`}
            id="InputUsername"
            placeholder={t("placeholder.enterUserName")}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />

          <small
            className={`form-text ${errors.username &&
              errors.username !== "Username is required."
              ? "text-danger"
              : "text-muted"
              }`}
          >
            {t("validation.usernameHelp")}
          </small>


          {errors.username === t("validation.usernameRequired") && (
            <div className="invalid-feedback d-block">{errors.username}</div>
          )}
        </div>



        <div className="form-group mb-3">
          <label htmlFor="InputEmail">
            Email {!isEdit && <span className="text-danger">*</span>}
          </label>
          <input
            type="email"
            className={`form-control ${errors.email ? "is-invalid" : ""}`}
            id="InputEmail"
            placeholder={t("placeholder.enterUserEmail")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {(errors.email || !email.trim()) && (
            <div className="invalid-feedback">
              {!email.trim() ? t("validation.emailRequired") : errors.email}
            </div>
          )}
        </div>


        <div className="form-group mb-3 position-relative">
          <label htmlFor="InputPassword">
            Password {!isEdit && <span className="text-danger">*</span>}
          </label>
          <div className="input-group">
            <input
              type={showPassword ? "text" : "password"}
              className={`form-control ${errors.password ? "is-invalid" : ""}`}
              id="InputPassword"
              placeholder={isEdit ? t("placeholder.enterNewPassword") : t("placeholder.enterPassword")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setShowPassword(!showPassword)}
              style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>


          <small
            className={`form-text ${errors.password &&
              errors.password !== "Password is required."
              ? "text-danger"
              : "text-muted"
              }`}
          >
            {t("validation.passwordHelp")}
          </small>


          {errors.password === t("validation.passwordRequired") && (
            <div className="invalid-feedback d-block">{errors.password}</div>
          )}
        </div>



        <div className="form-row mb-3">
          {canSetStaff && <div className="form-group flex-fill">
            <label htmlFor="InputStaff">Staff</label>
            <select
              className="form-control"
              id="InputStaff"
              value={is_staff ? "true" : "false"}
              onChange={(e) => setIsStaff(e.target.value === "true")}
            >
              <option value="true">{t("selectOption.yes")}</option>
              <option value="false">{t("selectOption.no")}</option>
            </select>
              <small className="form-text text-muted">{t("selectOption.selectRole")}</small>
          </div>}

          <div className="form-group flex-fill">
            <label htmlFor="InputRole">Role</label>
            <select
              className={`form-control ${errors.role ? "is-invalid" : ""}`}
              id="InputRole"
              value={role || ""}
              onChange={(e) => setRole(e.target.value)}
            >
              <option value="">{t("selectOption.selectRole")}</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
            {errors.role && <div className="invalid-feedback">{errors.role}</div>}
          </div>
        </div>


        <div className="form-row justify-content-end mt-4">
          {onClose && (
            <button type="button" className="btn btn-secondary me-2" onClick={onClose}>
              {t("button.cancel")}
            </button>
          )}
          <button type="submit" className="btn btn-primary">
            {isEdit ? t("button.updateUser") : t("button.registerUser")}
          </button>
        </div>
      </form>
    </div>
  );
}

export default RegistrationPage;
